import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles, categories } from "@/mocks/portal";
import { usePageMeta, injectJsonLd, clearPageJsonLd } from "@/hooks/usePageMeta";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const cat = categories.find((c) => c.slug === slug);
  const list = useMemo(() => articles.filter((a) => a.category === slug), [slug]);

  usePageMeta({
    title: cat ? `${cat.name}专栏 - 看料站 · ${cat.tagline}` : "栏目 - 看料站",
    description: cat
      ? `看料站${cat.name}栏目聚焦${cat.tagline}，提供${cat.name}方向值得一读的优质内容与深度解读，帮你快速找到本栏目最值得看的文章。`
      : "看料站内容栏目聚合页。",
    keywords: cat ? `看料站,${cat.name},${cat.name}资讯,${cat.name}精选,${cat.tagline}` : "看料站",
    path: `/column/${slug}`,
  });

  useEffect(() => {
    if (!cat) return;
    const json = {
      "@type": "CollectionPage",
      name: `${cat.name}专栏`,
      description: cat.tagline,
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: list.map((a, i) => ({ "@type": "ListItem", position: i + 1, name: a.title, url: `https://0ac9g.cn/article/${a.id}` })),
      },
    };
    const el = injectJsonLd(json);
    return () => clearPageJsonLd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat]);

  return (
    <div className="flex min-h-screen flex-col bg-background-50 text-foreground-900">
      <SiteHeader />

      <main className="flex-1">
        {cat ? (
          <>
            {/* 栏目头 */}
            <section className="relative overflow-hidden bg-background-100/80">
              <div className="mx-auto w-full max-w-[1180px] px-4 py-10 md:px-6 md:py-14">
                <nav className="text-xs text-foreground-500" aria-label="面包屑">
                  <Link to="/" className="hover:text-primary-700">
                    首页
                  </Link>
                  <i className="ri-arrow-right-s-line mx-1" />
                  <span className="text-foreground-800">{cat.name}</span>
                </nav>
                <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
                  <div>
                    <h1 className="font-heading text-3xl font-black text-foreground-950 md:text-4xl">{cat.name}专栏</h1>
                    <p className="mt-3 max-w-xl text-base text-foreground-600">{cat.tagline}。这里收录看料站精选的{cat.name}类内容，陪你筛选出真正值得花时间的一篇。</p>
                  </div>
                  <div className="flex items-center gap-2 rounded-full bg-background-50 border border-background-200/70 px-4 py-2 text-sm text-foreground-700">
                    <i className="ri-file-list-3-line text-primary-600" />
                    本栏目已收录 {list.length} 篇精选
                  </div>
                </div>
              </div>
            </section>

            {/* 跨栏目切换 */}
            <div className="sticky top-16 z-30 border-b border-background-200/70 bg-background-50/90 backdrop-blur md:top-[72px]">
              <div className="nice-scroll mx-auto flex max-w-[1180px] items-center gap-2 overflow-x-auto px-4 py-3 md:px-6">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/column/${c.slug}`}
                    className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                      c.slug === cat.slug
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-700"
                    }`}
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>

            <div className="mx-auto w-full max-w-[1180px] px-4 py-10 md:px-6 md:py-12">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </>
        ) : (
          <section className="mx-auto flex min-h-[60vh] max-w-[1180px] flex-col items-center justify-center px-4 text-center">
            <i className="ri-folder-4-line text-5xl text-background-400" />
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这个栏目</h1>
            <p className="mt-2 text-sm text-foreground-600">链接可能已失效，返回首页继续浏览精选内容吧。</p>
            <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
              <i className="ri-arrow-left-line" /> 返回首页
            </Link>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}